import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import WSButton from "../../Components/Button/WSButton";
import config from "../../Config/config.json";
import {
  callAPI,
  dateFormat,
  formatIndianCurrency,
  getLocalValue,
  LoacalVariables,
  Method,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";
import logo from "../../images/logo.svg";
import { IBranch } from "../DTO/Dto";

const InvPrint: React.FC = () => {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const { showMessage } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [branchData, setBranchData] = useState<IBranch>();

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("CRMInvoice", Method.GET, null, id);

        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }

    async function getBranchDetails() {
      try {
        const response: any = await callAPI(
          "Branch",
          Method.GET,
          null,
          getLocalValue(LoacalVariables.Branch)
        );
        if (response?.data) {
          setBranchData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    }

    if (id) {
      getDataById();
      getBranchDetails();
    }
  }, [id]);

  const handlePrint = useReactToPrint({
    contentRef,
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  if (!data) return <NotFound />;

  const totalAmount = data.quotationItems
    ?.reduce((sum: number, item: any) => sum + Number(item.totalPrice || 0), 0)
    .toFixed(2);

  const taxPercent = data?.tax || 0;
  const taxAmount = ((Number(totalAmount) * (taxPercent / 2)) / 100).toFixed(2);
  const grandTotal = (Number(totalAmount) + Number(taxAmount) * 2).toFixed(2);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={4}
      bgcolor="grey.100"
      minHeight="100vh"
    >
      {/* Print Button */}
      <WSButton
        id="printBtn"
        size="small"
        label="Print Invoice"
        onClick={handlePrint}
      />

      {/* Printable Content */}
      <Box
        ref={contentRef}
        mt={2}
        sx={{
          p: 4,
          width: "100%",
          maxWidth: "850px",
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        {/* Header */}
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Box display="flex" alignItems="center" gap={2}>
              <img
                src={logo}
                alt="Company Logo"
                width={100}
                height="auto"
                style={{ objectFit: "contain" }}
              />
            </Box>
          </Grid>
          <Grid item textAlign="right">
            <Typography variant="h6" fontWeight={700} color="error">
              {branchData?.name}
            </Typography>
            <Typography variant="body2">
              {branchData?.address?.addressLine1},{" "}
              {branchData?.address?.addressLine2}
            </Typography>
            <Typography variant="body2">
              P.O {branchData?.address?.pincode}, PH: {branchData?.phoneNumber}
            </Typography>
            <Typography variant="body2">
              GSTIN: {branchData?.gstNumber || "—"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Bill To */}
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Bill To
            </Typography>
            <Typography sx={{ fontSize: "0.875rem" }}>
              {data.customerName}
            </Typography>
            <Typography sx={{ fontSize: "0.875rem" }}>
              {data.address || "—"}
            </Typography>
            {/* <Typography sx={{ fontSize: "0.875rem" }}>
              Place Of Supply: {data.state || "—"}`
            </Typography> */}
          </Box>

          <Box textAlign="right">
            <Typography sx={{ fontSize: "0.875rem" }}>
              Invoice No: <strong>{data.invoiceNumber}</strong>
            </Typography>
            <Typography sx={{ fontSize: "0.875rem" }}>
              Date: {dateFormat(data.date, "DD/MM/YYYY")}
            </Typography>
            {/* <Typography sx={{ fontSize: "0.875rem" }}>
              Terms: Net {data.paymentTerms?.[0]?.days || 15}
            </Typography> */}
          </Box>
        </Box>

        {/* Items Table */}
        <TableContainer
          component={Paper}
          sx={{ mt: 3, borderRadius: 1, border: "1px solid #e0e0e0" }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8f8f8" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Item & Description</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Rate
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.quotationItems?.map((item: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: "0.85rem" }}>
                      {item.productName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">
                    {formatIndianCurrency(item.unitPrice)}
                  </TableCell>
                  <TableCell align="right">
                    {formatIndianCurrency(item.totalPrice)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals Section */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Box width="50%">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Sub Total</TableCell>
                  <TableCell align="right">
                    {formatIndianCurrency(totalAmount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>CGST {taxPercent / 2}%</TableCell>
                  <TableCell align="right">
                    {formatIndianCurrency(taxAmount)}
                  </TableCell>
                </TableRow>
                   <TableRow>
                  <TableCell>SGST {taxPercent / 2}%</TableCell>
                  <TableCell align="right">
                    {formatIndianCurrency(taxAmount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatIndianCurrency(grandTotal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Amount in Words */}
        <Box mt={1}>
          <Typography sx={{ fontSize: "0.875rem" }}>
            <strong>Total In Words:</strong> Rs {" "}
            {numberToWords(Number(grandTotal))} Only
          </Typography>
        </Box>

        {/* Notes Section */}
        {data.notes && (
          <Box mt={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              Notes
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", whiteSpace: "pre-line" }}>
              {data.notes}
            </Typography>
          </Box>
        )}

        {/* Bank Details */}
        <Box mt={3}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Bank Details
          </Typography>

          <Grid container spacing={0.5}>
            <Grid item xs={5}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                Account Name:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {data.bank?.bankHolderName || "-"}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                Bank:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {data.bank?.bankName || "-"}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                A/c No:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {data.bank?.accountNumber || "-"}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                Branch:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {data.bank?.branchName || "-"}
              </Typography>
            </Grid>

            <Grid item xs={5}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                IFSC:
              </Typography>
            </Grid>
            <Grid item xs={7}>
              <Typography sx={{ fontSize: "0.875rem" }}>
                {data.bank?.ifscCode || "-"}
              </Typography>
            </Grid>
          </Grid>
        </Box>


        <Divider sx={{ my: 3 }} />

        {/* Footer */}
        <Box textAlign="right">
          <Typography sx={{ fontSize: "0.875rem" }}>
            Authorized Signature
          </Typography>
          <Typography fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
            For {branchData?.name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InvPrint;

// Utility function for converting numbers to words
export function numberToWords(num: number): any {
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  if ((num = num || 0) === 0) return "Zero";
  if (num < 20) return a[num];
  if (num < 100)
    return b[Math.floor(num / 10)] + (num % 10 ? " " + a[num % 10] : "");
  if (num < 1000)
    return (
      a[Math.floor(num / 100)] +
      " Hundred " +
      (num % 100 ? numberToWords(num % 100) : "")
    );
  if (num < 100000)
    return (
      numberToWords(Math.floor(num / 1000)) +
      " Thousand " +
      numberToWords(num % 1000)
    );
  if (num < 10000000)
    return (
      numberToWords(Math.floor(num / 100000)) +
      " Lakh " +
      numberToWords(num % 100000)
    );
  return num.toString();
}
